import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api";
import RetentionRunMiniRow from "./RetentionRunMiniRow";
import { FaSearch } from "react-icons/fa";
import Sidebar from "./sidebar";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import SkeletonUILoader from "./SkeletonUILoader";

const RetentionRunsHistoryPage = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL;
  const navigate = useNavigate();

  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDatePopup, setShowDatePopup] = useState(false);

  const [filters, setFilters] = useState({
    status: "all",
    campaign: "",
  });

  const datePopupRef = useRef(null);

  useEffect(() => {
    fetchRuns();
  }, []);

  const fetchRuns = async () => {
    try {
      setLoading(true);

      const res = await api.get(
        `${baseUrl}/retention/runs/`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(
              "access_token"
            )}`,
          },
        }
      );

      setRuns(res.data.runs || []);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (
        datePopupRef.current &&
        !datePopupRef.current.contains(e.target)
      ) {
        setShowDatePopup(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick
      );
    };
  }, []);

  const filteredRuns = useMemo(() => {
    return runs.filter((run) => {
      if (
        filters.status !== "all" &&
        run.status.toLowerCase() !== filters.status
      ) {
        return false;
      }

      if (
        filters.campaign &&
        !String(run.run_li_id || run.id)
          .toLowerCase()
          .includes(filters.campaign.toLowerCase())
      ) {
        return false;
      }

      return true;
    });
  }, [runs, filters]);

  // LOADER
  if (loading) {
    return <SkeletonUILoader />;
  }

  return (
    <>
      <Helmet>
        <title>Recovery Campaign History</title>
        <meta
          name="description"
          content="Kadhr turns your sales data into repeat revenue opportunities."
        />
      </Helmet>

      <section className="container mtopcon dashb">
        <div className="row">
          <div className="col-12 col-lg-2 dash-min mb-3">
            <div className="nav-left-sidebar sidebar-dark pc">
              <div className="slimScrollDiv">
                <div className="menu-list">
                  <nav className="navbar navbar-expand-lg navbar-light nav-l-con">
                    <div
                      className="collapse navbar-collapse"
                      id="navbarNav"
                    >
                      <Sidebar />
                    </div>
                  </nav>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-lg-10 dash-max">
            <div className="row">
              <div className="container-fluid p-0">

                {/* HEADER */}
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-4">
                  <div>
                    <h3 className="fw800 mb-1">
                      Recovery Campaign History
                    </h3>

                    <div className="d-flex align-items-center gap-3 flex-wrap text-muted font8">
                      <div>
                        Review past recovery campaigns, delivery performance,
                        and projected revenue impact
                      </div>
                    </div>
                  </div>
                </div>

                {/* FILTER TOOLBAR */}
                <div className="retention-toolbar mb-4">
                  <div className="d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">

                    {/* STATUS FILTERS */}
                    <div className="d-flex gap-2 flex-wrap">

                      <button
                        onClick={() =>
                          setFilters({
                            ...filters,
                            status: "all",
                          })
                        }
                        className={`btn rounded-pill font8 px-3 ${
                          filters.status === "all"
                            ? "btn-dark"
                            : "btn-light border"
                        }`}
                      >
                        All ({runs.length})
                      </button>

                      <button
                        onClick={() =>
                          setFilters({
                            ...filters,
                            status: "executed",
                          })
                        }
                        className={`btn rounded-pill font8 px-3 ${
                          filters.status === "executed"
                            ? "btn-dark"
                            : "btn-light border"
                        }`}
                      >
                        Executed (
                        {
                          runs.filter(
                            (r) => r.status === "executed"
                          ).length
                        }
                        )
                      </button>

                      <button
                        onClick={() =>
                          setFilters({
                            ...filters,
                            status: "locked",
                          })
                        }
                        className={`btn rounded-pill font8 px-3 ${
                          filters.status === "locked"
                            ? "btn-dark"
                            : "btn-light border"
                        }`}
                      >
                        Locked (
                        {
                          runs.filter(
                            (r) => r.status === "locked"
                          ).length
                        }
                        )
                      </button>

                      <button
                        onClick={() =>
                          setFilters({
                            ...filters,
                            status: "pending",
                          })
                        }
                        className={`btn rounded-pill font8 px-3 ${
                          filters.status === "pending"
                            ? "btn-dark"
                            : "btn-light border"
                        }`}
                      >
                        Pending (
                        {
                          runs.filter(
                            (r) => r.status === "pending"
                          ).length
                        }
                        )
                      </button>
                    </div>

                    {/* SEARCH */}
                    <div className="toolbar-search">
                      <FaSearch
                        size={14}
                        className="search-icon"
                      />

                      <input
                        type="text"
                        placeholder="Search campaign..."
                        value={filters.campaign}
                        onChange={(e) =>
                          setFilters({
                            ...filters,
                            campaign: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </div>

                {/* RESULTS */}
                <div className="row g-3">
                  {filteredRuns.length > 0 ? (
                    filteredRuns.map((run) => (
                      <div
                        key={run.id}
                        className="col-12 col-lg-4"
                      >
                        <RetentionRunMiniRow
                          run={run}
                          active={false}
                          onClick={(selectedRun) => {
                            navigate(
                              `/seller/retarget/run/${selectedRun.id}`
                            );
                          }}
                        />
                      </div>
                    ))
                  ) : (
                    <div className="col-12">
                      <div className="text-center py-5 bg-white rounded-4 shadow-sm">
                        <h6 className="mb-1">
                          No campaigns found
                        </h6>

                        <p className="text-muted font8 mb-0">
                          Try adjusting your filters.
                        </p>
                      </div>
                    </div>
                  )}
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RetentionRunsHistoryPage;